import { useEffect, useState, useCallback, useMemo, useRef, Fragment } from "react";
import * as global from '../data/global';
import { registerAuth401Failure, clearAxiosAuthorizationHeader } from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../provider/authProvider.js";
import {
    cableDisplayLikeTemperatureHistory,
    getPdfReportBinNamesFromSiteData,
    getPdfReportCableNamesFromSiteData,
    getSiteInfosCableRows,
    getBinDisplayNameForSiteInfosCableId,
    resolveCableNameForReportApi,
} from '../data/pdfReportBinCableOptions.js';
import {
    outlinedActionButtonSx as getOutlinedActionButtonSx,
    containedActionButtonSx as getContainedActionButtonSx,
    withScrollHintMenuProps,
} from './windowChrome/index.js';

/** GraphQL / JSON may return ints as strings; Number.isFinite alone is not enough. */
function toOptionalFiniteInt(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
    const n = parseInt(String(value).trim(), 10);
    return Number.isFinite(n) ? n : null;
}

/** Server returns "HH:mm:ss" from BTS Task.RunAt (same weekday as forDate). */
function parseTaskTimeHms(s) {
    if (typeof s !== 'string' || !/^\d{1,2}:\d{2}/.test(s.trim())) return null;
    const parts = s.trim().split(':').map((x) => parseInt(x, 10));
    if (parts.length < 2 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) return null;
    const h = parts[0];
    const m = parts[1];
    const sec = parts[2] ?? 0;
    return new Date(2000, 0, 1, h, m, sec, 0);
}

/** Match history comparison: prefer ISO site data, else formatted lastScan (en-us locale). */
function parseDefaultTargetDateTimeFromSite(site) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let targetDate = new Date(today);
    let targetTime = new Date(2000, 0, 1, 12, 0, 0, 0);

    const dataLastScan = site?.data?.lastScan;
    const displayLastScan = site?.lastScan;
    if (dataLastScan) {
        const raw = typeof dataLastScan === 'string' ? dataLastScan : String(dataLastScan);
        const d = new Date(raw.includes('Z') ? raw.replace('Z', '-05:00') : raw);
        if (!Number.isNaN(d.getTime())) {
            targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            targetTime = new Date(
                2000, 0, 1,
                d.getHours(),
                d.getMinutes(),
                d.getSeconds(),
                d.getMilliseconds(),
            );
        }
    } else if (displayLastScan) {
        const tokens = String(displayLastScan).trim().split(/\s+/);
        if (tokens.length >= 2) {
            const datePart = tokens[0];
            const timeAndMeridiem = tokens.slice(1).join(' ');
            const dmy = datePart.split('-').map((t) => parseInt(t, 10));
            if (dmy.length === 3 && dmy.every(Number.isFinite)) {
                const [mm, dd, yy] = dmy;
                const year = yy < 100 ? 2000 + yy : yy;
                const combined = `${mm}/${dd}/${year} ${timeAndMeridiem}`;
                const d = new Date(combined);
                if (!Number.isNaN(d.getTime())) {
                    targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                    targetTime = new Date(
                        2000, 0, 1,
                        d.getHours(),
                        d.getMinutes(),
                        d.getSeconds(),
                        d.getMilliseconds(),
                    );
                }
            }
        }
    }
    return { targetDate, targetTime };
}

/** initializeCustomerSite stores Main on selectedSite.main and site.data.main */
function mergedSiteMain(site) {
    return { ...(site?.data?.main || {}), ...(site?.main || {}) };
}

/** First non-empty email string; treats null / whitespace as missing. */
function pickNonEmptyEmail(...candidates) {
    for (const v of candidates) {
        if (v == null) continue;
        const s = String(v);
        if (s.trim()) return s;
    }
    return '';
}

/**
 * Comparison from a site card calls `siteSelected`, which replaces `selectedSite` with a stub
 * that has the matching GUID but no Main row. Prefer a real `emailReportTo` from selectedSite
 * when present, otherwise the dashboard `siteInfos` copy of Main.EmailReportTo (same field Site Setup shows).
 */
function resolveEmailReportToForSite(siteGuid) {
    let fromSelected = '';
    if (sitesMatchGuid(global.gw.selectedSite?.siteGuid, siteGuid)) {
        const m = mergedSiteMain(global.gw.selectedSite);
        fromSelected = pickNonEmptyEmail(
            m?.emailReportTo,
            m?.EmailReportTo,
            global.gw.selectedSite?.emailReportTo,
        );
    }
    const entry = (global.gw.siteInfos || []).find(
        (s) => normalizeSiteGuid(s?.siteGuid) === normalizeSiteGuid(siteGuid),
    );
    const fromInfos = pickNonEmptyEmail(
        entry?.emailReportTo,
        entry?.EmailReportTo,
        entry?.main?.emailReportTo,
        entry?.main?.EmailReportTo,
    );
    return pickNonEmptyEmail(fromSelected, fromInfos);
}

/**
 * Sentinel for "All" in bin/cable filters (never sent to API).
 * Must not be '' — MUI Select does not update the displayed value reliably when value is empty until blur.
 */
const FILTER_ALL = '__GW_PDF_FILTER_ALL__';

/** `''` = all bins / all cables for helpers and server params. */
function binCableFilterParam(v) {
    return v === FILTER_ALL ? '' : v;
}

function numericSortKey(x) {
    if (x == null || x === '') return Number.POSITIVE_INFINITY;
    const n = Number(x);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function cableEntrySortKey(c) {
    if (c == null || typeof c !== 'object') return Number.POSITIVE_INFINITY;
    return numericSortKey(c.cableId ?? c.id ?? c.Id ?? c.CableId);
}

/**
 * Display name for a bin row. Prefer binName over name: getDefaultBin sets name to a composite
 * (siteName+binName) while data.bins / GraphQL use name — same order as resolving real ids below.
 */
function binLabel(b) {
    if (!b || typeof b !== 'object') return '';
    const v = b.binName ?? b.BinName ?? b.name ?? b.Name;
    return typeof v === 'string' ? v.trim() : String(v ?? '').trim();
}

/** BTS / GraphQL sometimes sends cableList as an object map instead of an array. */
function coerceCableList(raw) {
    var test = global.gw;
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const p = JSON.parse(raw);
            return Array.isArray(p) ? p : [];
        } catch {
            return [];
        }
    }
    if (typeof raw === 'object') {
        const n = raw.nodes ?? raw.Nodes ?? raw.items ?? raw.Items ?? raw.data ?? raw.Data;
        if (Array.isArray(n)) return n;
        const vals = Object.values(raw);
        if (vals.length > 0 && vals.every((x) => x != null && typeof x === 'object')) {
            return vals;
        }
    }
    return [];
}

function cableLabel(c) {
    if (c == null) return '';
    if (typeof c === 'string' || typeof c === 'number') return String(c).trim();
    if (typeof c !== 'object') return '';
    const n = c.name ?? c.Name ?? c.cableName ?? c.CableName;
    if (n != null && String(n).trim() !== '') return String(n).trim();
    const num = c.number ?? c.Number ?? c.cableNumber ?? c.CableNumber;
    if (num != null && String(num).trim() !== '') return String(num).trim();
    const id = c.id ?? c.Id ?? c.cableId ?? c.CableId;
    if (id != null && String(id).trim() !== '') return String(id).trim();
    const sn = c.sensorName ?? c.SensorName;
    if (sn != null && String(sn).trim() !== '') return String(sn).trim();
    const extras = ['label', 'Label', 'title', 'Title', 'displayName', 'DisplayName', 'text', 'Text'];
    for (const k of extras) {
        const v = c[k];
        if (typeof v === 'string' && v.trim() !== '') return v.trim();
    }
    const sl = c.sensorList ?? c.SensorList;
    if (Array.isArray(sl) && sl.length > 0) {
        const s0 = sl[0];
        const sn = s0?.name ?? s0?.Name;
        if (sn != null && String(sn).trim() !== '') return String(sn).trim();
    }
    return '';
}

/** Normalize layout bins from API into { binId, name, cableList: [{ name }] } for dropdowns. */
function normalizeLayoutBins(bins) {
    if (!Array.isArray(bins)) return [];
    return bins.map((b) => {
        const name = binLabel(b);
        // Real BTS id from API is data.bins[].binId; getDefaultBin duplicates it as binDataId and uses a generated binId for 3D.
        const binId = b.binDataId ?? b.BinDataId ?? b.binId ?? b.BinId ?? null;
        const rawList = coerceCableList(b.cableList ?? b.CableList ?? b.cables ?? b.Cables);
        const cableList = rawList
            .filter((c) => c != null)
            .map((c) => ({
                name: cableDisplayLikeTemperatureHistory(c) || cableLabel(c),
                cableId: c?.id ?? c?.Id ?? c?.cableId ?? c?.CableId ?? null,
            }));
        return { binId, name, cableList: cableList.filter((c) => c.name) };
    }).filter((row) => row.name);
}

function normalizeSiteGuid(g) {
    return String(g ?? '').replace(/[{}]/g, '').toLowerCase();
}

/** siteGuid values may differ by braces/casing between dropdown and `gw.selectedSite`. */
function sitesMatchGuid(a, b) {
    const x = normalizeSiteGuid(a);
    const y = normalizeSiteGuid(b);
    return x !== '' && x === y;
}

/** Bin dropdown merges names from API `name`, 3D `binName`, and layout — strict === hid all cables when labels differ slightly. */
function binNameMatchesFilter(wantBinLabel, ...candidates) {
    if (wantBinLabel == null || String(wantBinLabel).trim() === '') return true;
    const w = String(wantBinLabel).trim();
    for (const cand of candidates) {
        const t = String(cand ?? '').trim();
        if (!t) continue;
        if (t.localeCompare(w, undefined, { sensitivity: 'base' }) === 0) return true;
    }
    return false;
}

function rowBinDisplayName(b) {
    if (!b || typeof b !== 'object') return '';
    // Must match bin dropdown labels: binLabel prefers binName over getDefaultBin's composite `name`.
    return binLabel(b);
}

/** Works for merged dropdown rows `{ name, cableList }` and raw API / `getDefaultBin` bins. */
function collectCableNamesFromBinsInput(bins, filterBinName) {
    const out = [];
    if (!Array.isArray(bins)) return out;
    for (const b of bins) {
        if (
            !binNameMatchesFilter(
                filterBinName,
                b?.name,
                b?.Name,
                b?.binName,
                b?.BinName,
                rowBinDisplayName(b),
            )
        ) {
            continue;
        }
        const merged = b.cableList ?? b.CableList;
        if (
            Array.isArray(merged) &&
            merged.length > 0 &&
            merged[0] &&
            typeof merged[0] === 'object' &&
            Object.prototype.hasOwnProperty.call(merged[0], 'name')
        ) {
            for (const c of merged) {
                const lab = String(c?.name ?? '').trim();
                if (lab) out.push(lab);
            }
            continue;
        }
        const rawList = coerceCableList(b.cableList ?? b.CableList ?? b.cables ?? b.Cables);
        for (const c of rawList) {
            const lab = cableDisplayLikeTemperatureHistory(c) || cableLabel(c);
            if (lab) out.push(lab);
        }
    }
    return out;
}

/**
 * Merge several raw `bins` arrays (network + in-memory). fetchUserSite often has full cableList;
 * fetchSiteLayout can return bins with empty cableList — without merging, cables never appear.
 */
function mergeBinSources(rawBinArrays) {
    /** @type Map<string, { binId: *, name: string, cableNameToMinId: Map<string, number> }> */
    const map = new Map();
    for (const rawBins of rawBinArrays) {
        if (!Array.isArray(rawBins) || rawBins.length === 0) continue;
        for (const row of normalizeLayoutBins(rawBins)) {
            const key =
                row.binId != null && row.binId !== ''
                    ? `id:${String(row.binId)}`
                    : `name:${row.name}`;
            let entry = map.get(key);
            if (!entry) {
                entry = { binId: row.binId, name: row.name, cableNameToMinId: new Map() };
                map.set(key, entry);
            } else {
                if (!entry.name && row.name) entry.name = row.name;
                if ((entry.binId == null || entry.binId === '') && row.binId != null && row.binId !== '') {
                    entry.binId = row.binId;
                }
            }
            for (const c of row.cableList || []) {
                const cn = c?.name;
                if (!cn) continue;
                const cid = cableEntrySortKey(c);
                const prev = entry.cableNameToMinId.get(cn);
                if (prev === undefined || cid < prev) entry.cableNameToMinId.set(cn, cid);
            }
        }
    }
    return [...map.values()]
        .filter((e) => e.name)
        .map((e) => ({
            binId: e.binId,
            name: e.name,
            cableList: [...e.cableNameToMinId.entries()]
                .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
                .map(([name]) => ({ name })),
        }))
        .sort(
            (a, b) =>
                numericSortKey(a.binId) - numericSortKey(b.binId) ||
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
}

/**
 * Layout + siteInfos can introduce a second row for the same bin (e.g. name-key vs id-key).
 * MUI Select picks one name; .find() then returned the first row — often the one with no cables.
 */
/** First bin in merged layout rows whose cable list matches `cableDisplayName` (case-insensitive). */
function findBinNameForLegacyCable(siteLayoutBins, cableDisplayName) {
    if (!cableDisplayName || !Array.isArray(siteLayoutBins)) return null;
    const needle = String(cableDisplayName).trim();
    for (const b of siteLayoutBins) {
        for (const c of b.cableList || []) {
            const cn = c?.name;
            if (!cn) continue;
            if (cn.localeCompare(needle, undefined, { sensitivity: 'base' }) === 0) {
                return b.name || null;
            }
        }
    }
    return null;
}

function resolveBinNameInOptions(binName, binNameOptions) {
    if (!binName || !Array.isArray(binNameOptions)) return null;
    return (
        binNameOptions.find(
            (n) => n.localeCompare(binName, undefined, { sensitivity: 'base' }) === 0,
        ) ?? null
    );
}

function dedupeBinsByDisplayName(rows) {
    if (!Array.isArray(rows) || rows.length <= 1) return rows;
    /** @type Map<string, { name: string, binId: *, cableNameToMinId: Map<string, number> }> */
    const byName = new Map();
    for (const r of rows) {
        const key = String(r.name ?? '').trim().toLowerCase();
        if (!key) continue;
        let e = byName.get(key);
        if (!e) {
            e = { name: String(r.name).trim(), binId: r.binId, cableNameToMinId: new Map() };
            byName.set(key, e);
        } else {
            if ((e.binId == null || e.binId === '') && r.binId != null && r.binId !== '') {
                e.binId = r.binId;
            }
        }
        for (const c of r.cableList || []) {
            if (!c?.name) continue;
            const cid = cableEntrySortKey(c);
            const prev = e.cableNameToMinId.get(c.name);
            if (prev === undefined || cid < prev) e.cableNameToMinId.set(c.name, cid);
        }
    }
    return [...byName.values()]
        .map((e) => ({
            binId: e.binId,
            name: e.name,
            cableList: [...e.cableNameToMinId.entries()]
                .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
                .map(([name]) => ({ name })),
        }))
        .sort(
            (a, b) =>
                numericSortKey(a.binId) - numericSortKey(b.binId) ||
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
}

/** Synthetic bins from 3D layout rows when bin.cableList is empty but layoutCables has names. */
function rawBinsFromLayoutCables(layoutCables, dataBins, layoutBins) {
    if (!Array.isArray(layoutCables) || layoutCables.length === 0) return [];
    const idToName = new Map();
    if (Array.isArray(dataBins)) {
        for (const b of dataBins) {
            const id = b.binDataId ?? b.binId ?? b.BinId;
            const nm = binLabel(b);
            if (id != null && id !== '' && nm) idToName.set(String(id), nm);
        }
    }
    if (Array.isArray(layoutBins)) {
        for (const lb of layoutBins) {
            const id = lb.binId ?? lb.BinId;
            const nm = String(lb.binName ?? lb.BinName ?? '').trim();
            if (id != null && id !== '' && nm) idToName.set(String(id), nm);
        }
    }
    /** @type Map<string, Map<string, number>> */
    const cablesByBin = new Map();
    for (const lc of layoutCables) {
        const bid = lc.binId ?? lc.BinId;
        const cn = String(lc.cableName ?? lc.CableName ?? '').trim();
        if (bid == null || bid === '' || !cn) continue;
        const k = String(bid);
        const cid = numericSortKey(lc.cableId ?? lc.CableId ?? lc.id ?? lc.Id);
        if (!cablesByBin.has(k)) cablesByBin.set(k, new Map());
        const m = cablesByBin.get(k);
        const prev = m.get(cn);
        if (prev === undefined || cid < prev) m.set(cn, cid);
    }
    const out = [];
    for (const [bid, nameToId] of cablesByBin) {
        const displayName = idToName.get(bid);
        if (!displayName) continue;
        const binId = /^\d+$/.test(bid) ? parseInt(bid, 10) : bid;
        out.push({
            binId,
            name: displayName,
            cableList: [...nameToId.entries()]
                .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
                .map(([name]) => ({ name })),
        });
    }
    return out;
}

function formDefaultsForPdfSite(site) {
    const { targetDate, targetTime } = parseDefaultTargetDateTimeFromSite(site);
    const main = mergedSiteMain(site);
    const dtc = toOptionalFiniteInt(main.daysToCompare ?? main.DaysToCompare);
    const daysToCompare =
        dtc != null && dtc > 0
            ? dtc
            : (site?.temperatureComparison?.daysToCompare ?? 1);
    const p = toOptionalFiniteInt(main.plus ?? main.Plus);
    const m = toOptionalFiniteInt(main.minus ?? main.Minus);
    const plus = p != null ? p : (site?.temperatureComparison?.plus ?? 3);
    const minus = m != null ? m : (site?.temperatureComparison?.minus ?? 3);
    return {
        targetDate,
        targetTime,
        daysToCompare,
        plus: String(plus),
        minus: String(minus),
    };
}

const ReportTemperatureComparisonWindow = ({
    variant = 'overlay',
    onModalCancel,
    /** When set (e.g. after ModalChrome SiteSelectPhase), lock the form to this site and hide the Site dropdown. */
    lockedSiteGuid = null,
    /** Hide Cancel/View/Email footer — parent ModalChrome owns those actions instead. */
    hideFooterActions = false,
    /** Receives { onView, onEmail, loadingReport, loadingEmail } so the parent can put View/Email in its header. */
    onActionsChange = null,
}) => {
    const navigate = useNavigate();
    const { clearToken } = useAuth();
    const [loadingReport, setLoadingReport] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');
    const [forceUpdate, setForceUpdate] = useState(0);
    /** Bumps on site-ready / lastScan so bin/cable merge re-runs even if defaultsRefreshKey string is unchanged. */
    const [siteLayoutRefreshGeneration, setSiteLayoutRefreshGeneration] = useState(0);

    const siteOptions = useMemo(() => {
        const list = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
        if (list.length === 0 && global.gw.selectedSite?.siteGuid) {
            return [{
                siteGuid: global.gw.selectedSite.siteGuid,
                siteName: global.gw.selectedSite.siteName || 'Current site',
            }];
        }
        list.sort((a, b) => (a.siteName || '').toLowerCase().localeCompare((b.siteName || '').toLowerCase()));
        return list;
    }, [global.gw.siteInfos, global.gw.selectedSite?.siteGuid]);

    const [selectedSiteGuid, setSelectedSiteGuid] = useState(() =>
        lockedSiteGuid
            || global.gw.selectedSite?.siteGuid
            || siteOptions[0]?.siteGuid
            || '',
    );

    useEffect(() => {
        if (!lockedSiteGuid) return;
        if (!sitesMatchGuid(selectedSiteGuid, lockedSiteGuid)) {
            setSelectedSiteGuid(lockedSiteGuid);
        }
    }, [lockedSiteGuid, selectedSiteGuid]);

    const [daysToCompare, setDaysToCompare] = useState(() =>
        formDefaultsForPdfSite(global.gw.selectedSite).daysToCompare,
    );
    const [targetDate, setTargetDate] = useState(() =>
        formDefaultsForPdfSite(global.gw.selectedSite).targetDate,
    );
    const [targetTime, setTargetTime] = useState(() =>
        formDefaultsForPdfSite(global.gw.selectedSite).targetTime,
    );
    const [timeOffset, setTimeOffset] = useState(5);
    const [searchWindow, setSearchWindow] = useState(10);
    const [plus, setPlus] = useState(() => formDefaultsForPdfSite(global.gw.selectedSite).plus);
    const [minus, setMinus] = useState(() => formDefaultsForPdfSite(global.gw.selectedSite).minus);
    const [rawTitle, setRawTitle] = useState('');
    const [newTitle, setNewTitle] = useState('');
    const [filterBinName, setFilterBinName] = useState(FILTER_ALL);
    /** `''` = All; `si:{id}` from `siteInfos[].cables`; else legacy cable name string. */
    const [filterCableName, setFilterCableName] = useState(FILTER_ALL);
    const [siteLayoutBins, setSiteLayoutBins] = useState([]);
    const [reportError, setReportError] = useState('');
    /** Editable recipient list (initialized from `Main.emailReportTo` when the selected site matches). */
    const [reportEmailTo, setReportEmailTo] = useState('');
    const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [emailFeedback, setEmailFeedback] = useState('');

    useEffect(() => {
        setReportError('');
        setEmailFeedback('');
    }, [
        selectedSiteGuid,
        filterBinName,
        filterCableName,
        targetDate,
        targetTime,
        daysToCompare,
        timeOffset,
        searchWindow,
        plus,
        minus,
        rawTitle,
        newTitle,
    ]);

    // String sig so `siteInfos[].cables` updates recompute when `siteInfos` is mutated in place.
    const siteInfosCableSig = (global.gw.siteInfos || [])
        .map((s) => `${normalizeSiteGuid(s?.siteGuid)}:${(s.cables ?? []).map((c) => c?.id ?? c?.Id).join(',')}`)
        .join('|');

    const siteInfosCableRows = useMemo(
        () =>
            getSiteInfosCableRows(
                global.gw.siteInfos,
                selectedSiteGuid,
                binCableFilterParam(filterBinName),
            ),
        [selectedSiteGuid, filterBinName, siteInfosCableSig],
    );

    useEffect(() => {
        if (lockedSiteGuid) return;
        if (!selectedSiteGuid && siteOptions[0]?.siteGuid) {
            setSelectedSiteGuid(siteOptions[0].siteGuid);
        }
    }, [siteOptions, selectedSiteGuid, lockedSiteGuid]);

    const site = global.gw.selectedSite;
    const effMain = mergedSiteMain(site);
    // Prefer API-shaped bins (same as Temperature History); selectedSite.bins uses generated binId / composite name.
    const siteBinsForKey = sitesMatchGuid(site?.siteGuid, selectedSiteGuid)
        ? site?.data?.bins ?? site?.data?.Bins ?? site?.bins ?? site?.Bins
        : null;
    const siteBinsCableSig = Array.isArray(siteBinsForKey)
        ? siteBinsForKey
            .map((b) => {
                const bid = b?.binDataId ?? b?.BinDataId ?? b?.binId ?? b?.BinId ?? '';
                return `${bid}:${(b?.cableList ?? b?.CableList ?? []).length}`;
            })
            .join('|')
        : '';
    const layoutCableSig = sitesMatchGuid(site?.siteGuid, selectedSiteGuid)
        ? `${(site?.layoutCables ?? site?.LayoutCables ?? []).length}|${(site?.layoutSensors ?? site?.LayoutSensors ?? []).length}`
        : '';
    const defaultsRefreshKey = sitesMatchGuid(site?.siteGuid, selectedSiteGuid)
        ? [
                site?.data?.lastScan ?? '',
                site?.lastScan ?? '',
                effMain.daysToCompare ?? effMain.DaysToCompare ?? '',
                effMain.plus ?? effMain.Plus ?? '',
                effMain.minus ?? effMain.Minus ?? '',
                String(effMain.emailReportTo ?? effMain.EmailReportTo ?? ''),
                site?.isSiteInitialized ? '1' : '0',
                siteBinsCableSig,
                layoutCableSig,
            ].join('|')
        : selectedSiteGuid;

    // String sig so `siteInfos[].emailReportTo` updates recompute when `siteInfos` is mutated in place.
    const siteInfosEmailSig = (global.gw.siteInfos || [])
        .map((s) => `${normalizeSiteGuid(s?.siteGuid)}:${String(s?.emailReportTo ?? '').slice(0, 500)}`)
        .join('|');

    useEffect(() => {
        setReportEmailTo(resolveEmailReportToForSite(selectedSiteGuid));
    }, [selectedSiteGuid, defaultsRefreshKey, siteInfosEmailSig]);

    useEffect(() => {
        const bump = () => {
            setForceUpdate((x) => x + 1);
            setSiteLayoutRefreshGeneration((g) => g + 1);
        };
        window.addEventListener('grainwaveSelectedSiteReady', bump);
        window.addEventListener('lastScanUpdated', bump);
        return () => {
            window.removeEventListener('grainwaveSelectedSiteReady', bump);
            window.removeEventListener('lastScanUpdated', bump);
        };
    }, []);

    useEffect(() => {
        if (!selectedSiteGuid) return;
        const current = global.gw.selectedSite;
        if (sitesMatchGuid(current?.siteGuid, selectedSiteGuid)) {
            const d = formDefaultsForPdfSite(current);
            setTargetDate(d.targetDate);
            setTargetTime(d.targetTime);
            setDaysToCompare(d.daysToCompare);
            setPlus(d.plus);
            setMinus(d.minus);
            return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setTargetDate(today);
        setTargetTime(new Date(2000, 0, 1, 12, 0, 0, 0));
        setDaysToCompare(1);
        setPlus('3');
        setMinus('3');
    }, [selectedSiteGuid, defaultsRefreshKey]);

    useEffect(() => {
        setFilterBinName(FILTER_ALL);
        setFilterCableName(FILTER_ALL);
        if (!selectedSiteGuid) {
            setSiteLayoutBins([]);
            return;
        }
        let cancelled = false;
        const output = global.gw.bolts ? 'compressed' : 'direct';
        (async () => {
            const wantGuid = normalizeSiteGuid(selectedSiteGuid);
            const rawSources = [];
            try {
                // Same as Temperature History: prefer data.bins; fall back to 3D bins if data not shaped yet.
                if (sitesMatchGuid(global.gw.selectedSite?.siteGuid, selectedSiteGuid)) {
                    const ss = global.gw.selectedSite;
                    const dataBins = ss.data?.bins ?? ss.data?.Bins;
                    const threeBins = ss.bins ?? ss.Bins;
                    // Push both: merge keys differ (binId vs binDataId); one path may carry cableList the other misses.
                    if (Array.isArray(dataBins) && dataBins.length > 0) {
                        rawSources.push(dataBins);
                    }
                    if (Array.isArray(threeBins) && threeBins.length > 0) {
                        rawSources.push(threeBins);
                    }
                }
                // Do not let a failed layout request wipe in-memory bins (previous bug: catch → setSiteLayoutBins([])).
                try {
                    const layout = await createApiEndpoint().fetchSiteLayout(output, selectedSiteGuid);
                    if (!cancelled) {
                        const layoutBins = layout?.bins ?? layout?.Bins;
                        if (Array.isArray(layoutBins) && layoutBins.length > 0) {
                            rawSources.push(layoutBins);
                        }
                    }
                } catch {
                    /* network / auth; keep rawSources from memory */
                }
                if (cancelled) return;
                const fromInfos = (global.gw.siteInfos || []).find(
                    (s) => normalizeSiteGuid(s?.siteGuid) === wantGuid,
                );
                const binsFromInfos = fromInfos?.bins ?? fromInfos?.Bins;
                if (Array.isArray(binsFromInfos) && binsFromInfos.length > 0) {
                    rawSources.push(binsFromInfos);
                }
                if (sitesMatchGuid(global.gw.selectedSite?.siteGuid, selectedSiteGuid)) {
                    const ss = global.gw.selectedSite;
                    const lc = ss?.layoutCables ?? ss?.LayoutCables;
                    const dataBins = ss?.data?.bins ?? ss?.data?.Bins;
                    const layoutBinsMem = ss?.layoutBins ?? ss?.LayoutBins;
                    const fromLc = rawBinsFromLayoutCables(lc, dataBins, layoutBinsMem);
                    if (fromLc.length > 0) rawSources.push(fromLc);
                }
                const next = dedupeBinsByDisplayName(mergeBinSources(rawSources));
                setSiteLayoutBins(next);
            } catch {
                if (!cancelled) setSiteLayoutBins([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedSiteGuid, defaultsRefreshKey, siteLayoutRefreshGeneration]);

    const binNameOptions = useMemo(() => {
        const ss = global.gw.selectedSite;
        const fromLayout = siteLayoutBins.map((b) => b?.name).filter(Boolean);
        if (sitesMatchGuid(ss?.siteGuid, selectedSiteGuid)) {
            const live = getPdfReportBinNamesFromSiteData(ss);
            const seen = new Set(fromLayout.map((n) => n.toLowerCase()));
            const rest = live.filter((n) => !seen.has(n.toLowerCase()));
            return [...fromLayout, ...rest];
        }
        return fromLayout;
    }, [
        siteLayoutBins,
        selectedSiteGuid,
        defaultsRefreshKey,
        siteLayoutRefreshGeneration,
    ]);

    /** When `siteInfos[].cables` is empty for this site, same as Temperature History / layout bins. */
    const legacyCableNameOptions = useMemo(() => {
        const ss = global.gw.selectedSite;
        const binParam = binCableFilterParam(filterBinName);
        const fromLayout = collectCableNamesFromBinsInput(siteLayoutBins, binParam);
        if (sitesMatchGuid(ss?.siteGuid, selectedSiteGuid)) {
            const live = getPdfReportCableNamesFromSiteData(ss, binParam);
            const seen = new Set(fromLayout.map((n) => n.toLowerCase()));
            const rest = live.filter((n) => !seen.has(n.toLowerCase()));
            return [...fromLayout, ...rest];
        }
        return fromLayout;
    }, [
        siteLayoutBins,
        filterBinName,
        selectedSiteGuid,
        defaultsRefreshKey,
        siteLayoutRefreshGeneration,
    ]);

    const useSiteInfosCablesUi = siteInfosCableRows.length > 0;

    const handleCableFilterChange = useCallback(
        (value) => {
            if (value === FILTER_ALL) {
                setFilterCableName(FILTER_ALL);
                return;
            }
            if (filterBinName === FILTER_ALL) {
                let resolvedBin = null;
                if (String(value).startsWith('si:')) {
                    const id = String(value).slice(3);
                    const binName = getBinDisplayNameForSiteInfosCableId(
                        global.gw.siteInfos,
                        selectedSiteGuid,
                        id,
                    );
                    resolvedBin = resolveBinNameInOptions(binName, binNameOptions);
                } else {
                    const binName = findBinNameForLegacyCable(siteLayoutBins, value);
                    resolvedBin = resolveBinNameInOptions(binName, binNameOptions);
                }
                if (resolvedBin) {
                    setFilterBinName(resolvedBin);
                }
            }
            setFilterCableName(value);
        },
        [filterBinName, binNameOptions, siteLayoutBins, selectedSiteGuid],
    );

    useEffect(() => {
        if (filterCableName === FILTER_ALL) return;
        if (useSiteInfosCablesUi) {
            const ok = siteInfosCableRows.some((r) => `si:${r.id}` === filterCableName);
            if (!ok) setFilterCableName(FILTER_ALL);
            return;
        }
        if (!legacyCableNameOptions.includes(filterCableName)) {
            setFilterCableName(FILTER_ALL);
        }
    }, [legacyCableNameOptions, filterCableName, siteInfosCableRows, useSiteInfosCablesUi]);

    useEffect(() => {
        if (!selectedSiteGuid || !targetDate) return;
        let cancelled = false;
        const output = global.gw.bolts ? 'compressed' : 'direct';
        (async () => {
            const res = await createApiEndpoint().fetchSiteComparisonFormDefaults(output, selectedSiteGuid, targetDate);
            if (cancelled || res === false) return;
            if (res?.errors?.length) return;
            const row = res?.data?.fetchSiteComparisonFormDefaults;
            if (!row || typeof row !== 'object') return;
            const dtc = toOptionalFiniteInt(row.daysToCompare ?? row.DaysToCompare);
            if (dtc != null && dtc > 0) setDaysToCompare(dtc);
            const p = toOptionalFiniteInt(row.plus ?? row.Plus);
            if (p != null) setPlus(String(p));
            const m = toOptionalFiniteInt(row.minus ?? row.Minus);
            if (m != null) setMinus(String(m));
            const tt = parseTaskTimeHms(row.taskTimeHms ?? row.TaskTimeHms);
            if (tt) setTargetTime(tt);
        })();
        return () => { cancelled = true; };
    }, [selectedSiteGuid, targetDate]);

    const getThemeColor = useCallback((lightColor, darkColor) => {
        return currentTheme === 'light' ? lightColor : darkColor;
    }, [currentTheme]);

    useEffect(() => {
        const onTheme = () => {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate((x) => x + 1);
        };
        window.addEventListener('themeChanged', onTheme);
        return () => window.removeEventListener('themeChanged', onTheme);
    }, []);

    useEffect(() => {
        const t = setInterval(() => {
            const v = global.gw.theme || 'dark';
            if (v !== currentTheme) {
                setCurrentTheme(v);
                setForceUpdate((x) => x + 1);
            }
        }, 100);
        return () => clearInterval(t);
    }, [currentTheme]);

    const theme = useMemo(() => {
        const isLight = currentTheme === 'light';
        return MuiMaterials.createTheme({
            palette: {
                mode: isLight ? 'light' : 'dark',
                text: {
                    primary: isLight ? '#333333' : '#f4cc8e',
                    secondary: isLight ? '#666666' : '#d4b080',
                },
                background: {
                    paper: isLight ? '#ffffff' : '#2d2d2d',
                    default: isLight ? '#fafafa' : '#1e1e1e',
                },
            },
            components: {
                MuiMenu: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: isLight ? '#ffffff' : '#2d2d2d',
                            color: isLight ? '#333333' : '#f4cc8e',
                            border: `1px solid ${isLight ? '#cccccc' : '#f4cc8e4d'}`,
                        },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            fontSize: '14px',
                            color: isLight ? '#333333' : '#f4cc8e',
                            '&.Mui-selected': {
                                backgroundColor: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(244, 204, 142, 0.18)',
                                color: isLight ? '#333333' : '#f4cc8e',
                                '&:hover': {
                                    backgroundColor: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(244, 204, 142, 0.24)',
                                },
                            },
                            '&:hover': {
                                backgroundColor: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(244, 204, 142, 0.1)',
                            },
                        },
                    },
                },
                MuiSelect: {
                    styleOverrides: {
                        select: {
                            color: isLight ? '#333333' : '#f4cc8e',
                            fontSize: '14px',
                        },
                        icon: {
                            color: isLight ? '#666666' : '#a8abb1',
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#666666' : '#a8abb1',
                            fontSize: '14px',
                            '&.Mui-focused': {
                                color: isLight ? '#333333' : '#f4cc8e',
                            },
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: '14px',
                            textTransform: 'none',
                            background: isLight ? '#e0e0e0' : '#2d2d2d',
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeMedium: {
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#333333' : '#f4cc8e',
                            backgroundColor: isLight ? '#ffffff' : '#2d2d2d',
                            fontSize: '14px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${isLight ? '#cccccc' : 'rgba(244,204,142,0.5)'}`,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${isLight ? '#666666' : '#f4cc8e'}`,
                            },
                        },
                        input: {
                            fontSize: '14px',
                            padding: '8.5px 14px',
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '.MuiInputLabel-root.Mui-focused': {
                                color: isLight ? '#333333' : '#f4cc8e',
                            },
                        },
                    },
                },
            },
        });
    }, [currentTheme, forceUpdate]);

    const selectedSiteName = useMemo(() => {
        const s = siteOptions.find((x) => x.siteGuid === selectedSiteGuid);
        return s?.siteName || global.gw.selectedSite?.siteName || '';
    }, [siteOptions, selectedSiteGuid]);

    // Match Site Inventory: muted outline, field background, compact 14px type — not the old
    // bright gold / 18px chrome that made these fields look oversized.
    const fieldSx = useMemo(() => ({
        '& .MuiOutlinedInput-root': {
            color: getThemeColor('#333333', '#f4cc8e'),
            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
            fontSize: '14px',
            '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
            '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
        },
        '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1'), fontSize: '14px' },
        '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
        '& .MuiInputBase-input': { color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px' },
        '& .MuiSelect-select': { color: getThemeColor('#333333', '#f4cc8e'), fontSize: '14px' },
        '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
    }), [getThemeColor]);

    const selectMenuProps = useMemo(() => ({
        MenuProps: withScrollHintMenuProps({
            PaperProps: {
                className: 'hide-scrollbar',
                sx: {
                    backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                    color: getThemeColor('#333333', '#f4cc8e'),
                    '& .MuiMenuItem-root': {
                        color: getThemeColor('#333333', '#f4cc8e'),
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: getThemeColor('rgba(0, 0, 0, 0.04)', 'rgba(244, 204, 142, 0.1)'),
                        },
                        '&.Mui-selected': {
                            color: getThemeColor('#333333', '#f4cc8e'),
                            backgroundColor: getThemeColor('rgba(0, 0, 0, 0.08)', 'rgba(244, 204, 142, 0.18)'),
                            '&:hover': {
                                backgroundColor: getThemeColor('rgba(0, 0, 0, 0.12)', 'rgba(244, 204, 142, 0.24)'),
                            },
                        },
                    },
                },
            },
        }),
    }), [getThemeColor]);

    const outlinedActionButtonSx = useMemo(
        () => getOutlinedActionButtonSx(),
        [getThemeColor, forceUpdate],
    );

    /** Contained primary actions; explicit selectors override ThemeProvider MuiButton root + primary palette. */
    const containedActionButtonSx = useMemo(
        () => getContainedActionButtonSx(),
        [currentTheme, forceUpdate, getThemeColor],
    );

    const pdfFromReportValue = (value) => {
        if (value == null || typeof value !== 'string') {
            return { ok: false, message: 'No report data from server.' };
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return { ok: false, message: 'No report data from server.' };
        }
        let bytes;
        try {
            bytes = global.base64StringToArrayBuffer(trimmed);
        } catch {
            return { ok: false, message: trimmed };
        }
        if (!bytes || bytes.byteLength < 5) {
            return { ok: false, message: trimmed };
        }
        const u = new Uint8Array(bytes);
        const isPdf = u[0] === 0x25 && u[1] === 0x50 && u[2] === 0x44 && u[3] === 0x46;
        if (!isPdf) {
            try {
                const text = new TextDecoder().decode(u.slice(0, 400));
                const ascii = text.replace(/\u0000/g, '').trim();
                if (ascii.length > 0 && /^[\x09\x0a\x0d\x20-\x7e]+$/.test(ascii)) {
                    return { ok: false, message: ascii };
                }
            } catch { /* ignore */ }
            return { ok: false, message: 'Server returned data that is not a PDF (check API / report assets on the host).' };
        }
        return { ok: true, bytes };
    };

    const handleViewPdf = () => {
        if (!selectedSiteGuid) return;
        setReportError('');
        setLoadingReport(true);
        const reportWindow = window.open('', '_blank');

        const dateOnly = targetDate ? new Date(targetDate) : new Date();
        dateOnly.setHours(0, 0, 0, 0);

        const t = targetTime ? new Date(targetTime) : new Date();
        const timeOnly = new Date(2000, 0, 1, t.getHours(), t.getMinutes(), t.getSeconds(), t.getMilliseconds());

        const plusN = plus.trim() === '' ? null : Number(plus);
        const minusN = minus.trim() === '' ? null : Number(minus);

        const cableNameForApi =
            filterCableName !== FILTER_ALL
                ? resolveCableNameForReportApi(global.gw.siteInfos, selectedSiteGuid, filterCableName)
                : null;

        createApiEndpoint()
            .fetchReportTemperatureComparisonData(
                'compressed',
                selectedSiteGuid,
                daysToCompare ?? null,
                dateOnly,
                timeOnly,
                Number(timeOffset),
                Number(searchWindow),
                Number.isFinite(plusN) ? plusN : null,
                Number.isFinite(minusN) ? minusN : null,
                rawTitle.trim() ? rawTitle.trim() : null,
                newTitle.trim() ? newTitle.trim() : null,
                filterBinName !== FILTER_ALL ? filterBinName : null,
                cableNameForApi,
            )
            .then((response) => {
                if (response === false && global.gw.token && global.gw.auth) {
                    const authError = new Error('401 Unauthorized - Server logged out user');
                    authError.response = { status: 401 };
                    throw authError;
                }
                const gqlErr = response?.errors?.[0]?.message;
                if (gqlErr) {
                    if (reportWindow) reportWindow.close();
                    setReportError(gqlErr);
                    setLoadingReport(false);
                    return;
                }
                const rawVal = response?.data?.fetchReportTemperatureComparisonData?.value;
                const decoded = pdfFromReportValue(rawVal);
                if (!decoded.ok) {
                    if (reportWindow) reportWindow.close();
                    setReportError(decoded.message);
                    setLoadingReport(false);
                    return;
                }
                const blob = new Blob([decoded.bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                if (reportWindow) reportWindow.location.href = url;
                else window.open(url, '_blank');
                setLoadingReport(false);
            })
            .catch((error) => {
                if (error.response && error.response.status === 401) {
                    if (error.__gw401HandledByInterceptor || error.__gw401SessionLoggedOut) {
                        if (reportWindow) reportWindow.close();
                        setLoadingReport(false);
                        return;
                    }
                    if (registerAuth401Failure()) {
                        clearToken();
                        clearAxiosAuthorizationHeader();
                        global.gw.token = null;
                        global.gw.auth = null;
                        global.gw.isAuthenticated = false;
                        setTimeout(() => { window.location.href = '/emailLogin'; }, 100);
                    }
                    return;
                }
                if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
                    if (error.__gw401HandledByInterceptor || error.__gw401SessionLoggedOut) {
                        if (reportWindow) reportWindow.close();
                        setLoadingReport(false);
                        return;
                    }
                    if (registerAuth401Failure()) {
                        clearToken();
                        clearAxiosAuthorizationHeader();
                        global.gw.token = null;
                        global.gw.auth = null;
                        global.gw.isAuthenticated = false;
                        setTimeout(() => { window.location.href = '/emailLogin'; }, 100);
                    }
                    return;
                }
                if (reportWindow) reportWindow.close();
                setLoadingReport(false);
            });
    };

    const requestEmailReport = () => {
        setEmailFeedback('');
        if (!selectedSiteGuid) return;
        const to = reportEmailTo.trim();
        if (!to) {
            setEmailFeedback('Enter at least one email address.');
            return;
        }
        setEmailConfirmOpen(true);
    };

    const confirmSendEmailReport = () => {
        setEmailFeedback('');
        if (!selectedSiteGuid) {
            setEmailConfirmOpen(false);
            return;
        }
        const to = reportEmailTo.trim();
        if (!to) {
            setEmailFeedback('Enter at least one email address.');
            setEmailConfirmOpen(false);
            return;
        }

        setLoadingEmail(true);

        const dateOnly = targetDate ? new Date(targetDate) : new Date();
        dateOnly.setHours(0, 0, 0, 0);

        const t = targetTime ? new Date(targetTime) : new Date();
        const timeOnly = new Date(2000, 0, 1, t.getHours(), t.getMinutes(), t.getSeconds(), t.getMilliseconds());

        const plusN = plus.trim() === '' ? null : Number(plus);
        const minusN = minus.trim() === '' ? null : Number(minus);

        const cableNameForApi =
            filterCableName !== FILTER_ALL
                ? resolveCableNameForReportApi(global.gw.siteInfos, selectedSiteGuid, filterCableName)
                : null;

        createApiEndpoint()
            .fetchEmailReportTemperatureComparisonData(
                'compressed',
                to,
                selectedSiteGuid,
                daysToCompare ?? null,
                dateOnly,
                timeOnly,
                Number(timeOffset),
                Number(searchWindow),
                Number.isFinite(plusN) ? plusN : null,
                Number.isFinite(minusN) ? minusN : null,
                rawTitle.trim() ? rawTitle.trim() : null,
                newTitle.trim() ? newTitle.trim() : null,
                filterBinName !== FILTER_ALL ? filterBinName : null,
                cableNameForApi,
            )
            .then((response) => {
                if (response === false && global.gw.token && global.gw.auth) {
                    const authError = new Error('401 Unauthorized - Server logged out user');
                    authError.response = { status: 401 };
                    throw authError;
                }
                const gqlErr = response?.errors?.[0]?.message;
                if (gqlErr) {
                    setEmailFeedback(gqlErr);
                    setLoadingEmail(false);
                    setEmailConfirmOpen(false);
                    return;
                }
                const ok = response?.data?.fetchEmailReportTemperatureComparisonData === true;
                if (ok) {
                    setEmailFeedback('Report emailed.');
                    setEmailConfirmOpen(false);
                } else {
                    setEmailFeedback(
                        typeof response?.data?.fetchEmailReportTemperatureComparisonData === 'boolean'
                            ? 'The server did not confirm the email was sent.'
                            : 'Could not send email (check connection or server).',
                    );
                    setEmailConfirmOpen(false);
                }
                setLoadingEmail(false);
            })
            .catch((error) => {
                setLoadingEmail(false);
                if (error.response && error.response.status === 401) {
                    if (error.__gw401HandledByInterceptor || error.__gw401SessionLoggedOut) {
                        setEmailConfirmOpen(false);
                        return;
                    }
                    if (registerAuth401Failure()) {
                        clearToken();
                        clearAxiosAuthorizationHeader();
                        global.gw.token = null;
                        global.gw.auth = null;
                        global.gw.isAuthenticated = false;
                        setEmailConfirmOpen(false);
                        setTimeout(() => { window.location.href = '/emailLogin'; }, 100);
                    }
                    return;
                }
                if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
                    if (error.__gw401HandledByInterceptor || error.__gw401SessionLoggedOut) {
                        setEmailConfirmOpen(false);
                        return;
                    }
                    if (registerAuth401Failure()) {
                        clearToken();
                        clearAxiosAuthorizationHeader();
                        global.gw.token = null;
                        global.gw.auth = null;
                        global.gw.isAuthenticated = false;
                        setEmailConfirmOpen(false);
                        setTimeout(() => { window.location.href = '/emailLogin'; }, 100);
                    }
                    return;
                }
                setEmailFeedback(error?.message || 'Could not send email.');
                setEmailConfirmOpen(false);
            });
    };

    // Keep latest View/Email handlers in a ref so the parent header can call them without
    // rebinding every time form fields change.
    const actionsRef = useRef({ onView: () => {}, onEmail: () => {} });
    actionsRef.current = { onView: handleViewPdf, onEmail: requestEmailReport };
    useEffect(() => {
        if (!onActionsChange) return undefined;
        onActionsChange({
            onView: () => actionsRef.current.onView?.(),
            onEmail: () => actionsRef.current.onEmail?.(),
            loadingReport,
            loadingEmail,
        });
        return () => onActionsChange(null);
    }, [onActionsChange, loadingReport, loadingEmail]);

    const formBody = (
        <>
            {variant !== 'modal' && (
                <MuiMaterials.Typography
                    sx={{ color: getThemeColor('#333333', '#f4cc8e'), fontWeight: 700, marginBottom: '12px' }}
                >
                    Site comparison (on-server PDF)
                </MuiMaterials.Typography>
            )}
            <MuiMaterials.ThemeProvider theme={theme}>
                        <MuiMaterials.Box
                            sx={{
                                display: 'grid',
                                width: '100%',
                                gap: 1.5,
                                alignItems: 'start',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(80px, 1fr))',
                                    sm: 'repeat(4, minmax(80px, 1fr))',
                                    md: 'repeat(6, minmax(80px, 1fr))',
                                    lg: 'repeat(8, minmax(80px, 1fr))',
                                },
                            }}
                        >
                        {!lockedSiteGuid ? (
                        <MuiMaterials.FormControl
                            size="small"
                            sx={{
                                ...fieldSx,
                                gridColumn: '1 / -1',
                                minWidth: 80,
                                width: '100%',
                                maxWidth: Math.max(
                                    200,
                                    ...siteOptions.map((s) => String(s.siteName || s.siteGuid || '').length * 8 + 48),
                                ),
                            }}
                        >
                            <MuiMaterials.InputLabel id="tc-pdf-site-label">Site</MuiMaterials.InputLabel>
                            <MuiMaterials.Select
                                labelId="tc-pdf-site-label"
                                label="Site"
                                value={selectedSiteGuid || ''}
                                onChange={(e) => setSelectedSiteGuid(e.target.value)}
                                {...selectMenuProps}
                            >
                                {siteOptions.map((s) => (
                                    <MuiMaterials.MenuItem key={s.siteGuid} value={s.siteGuid}>
                                        {s.siteName || s.siteGuid}
                                    </MuiMaterials.MenuItem>
                                ))}
                            </MuiMaterials.Select>
                        </MuiMaterials.FormControl>
                        ) : null}

                        <MuiMaterials.LocalizationProvider dateAdapter={MuiMaterials.AdapterDateFns}>
                                <MuiMaterials.DatePicker
                                    label="Target date"
                                    value={targetDate}
                                    onChange={(v) => v && setTargetDate(v)}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                '.MuiDayCalendar-weekDayLabel': { color: getThemeColor('#333333', '#f4cc8e') },
                                                '.MuiPaper-root': {
                                                    border: `1px solid ${getThemeColor('#cccccc', 'rgba(244,204,142,0.5)')}`,
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                '.MuiButtonBase-root': { color: getThemeColor('#333333', '#f4cc8e') },
                                                '.MuiPickersDay-root.Mui-selected': {
                                                    background: getThemeColor('#e0e0e0', 'black'),
                                                },
                                            },
                                        },
                                    }}
                                    sx={{ ...fieldSx, gridColumn: 'span 2', minWidth: 80, width: '100%' }}
                                />
                                <MuiMaterials.TimePicker
                                    label="Target time"
                                    value={targetTime}
                                    onChange={(v) => v && setTargetTime(v)}
                                    slotProps={{
                                        popper: {
                                            sx: {
                                                '.MuiPaper-root': {
                                                    border: `1px solid ${getThemeColor('#cccccc', 'rgba(244,204,142,0.5)')}`,
                                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                            },
                                        },
                                    }}
                                    sx={{ ...fieldSx, gridColumn: 'span 2', minWidth: 80, width: '100%' }}
                                />
                        </MuiMaterials.LocalizationProvider>

                            {[
                                { label: 'Days to compare', val: daysToCompare, set: setDaysToCompare, step: 1, span: 2 },
                                {
                                    label: 'Time offset (hours)',
                                    val: timeOffset,
                                    set: setTimeOffset,
                                    step: 0.5,
                                    span: 2,
                                    tooltip:
                                        'Filters out scans that are not within this window. Lower value will have a more accurate comparison but may leave out some dates.',
                                },
                                {
                                    label: 'Search window (days)',
                                    val: searchWindow,
                                    set: setSearchWindow,
                                    step: 1,
                                    span: 2,
                                    tooltip:
                                        'Includes scans within this window if the scans did not report every day.',
                                },
                                { label: 'Plus', val: plus, set: setPlus, step: null, span: 1 },
                                { label: 'Minus', val: minus, set: setMinus, step: null, span: 1 },
                            ].map((f) => {
                                const field = (
                                    <MuiMaterials.TextField
                                        label={f.label}
                                        type={f.step != null ? 'number' : 'text'}
                                        size="small"
                                        value={f.val}
                                        onChange={(e) => {
                                            if (f.step == null) {
                                                f.set(e.target.value);
                                                return;
                                            }
                                            const n = f.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10);
                                            f.set(Number.isFinite(n) ? n : f.val);
                                        }}
                                        sx={{
                                            ...fieldSx,
                                            minWidth: 80,
                                            width: '100%',
                                        }}
                                        inputProps={f.step != null ? { step: f.step } : undefined}
                                    />
                                );
                                return (
                                    <MuiMaterials.Box
                                        key={f.label}
                                        sx={{ gridColumn: `span ${f.span}`, minWidth: 80, width: '100%' }}
                                    >
                                        {f.tooltip ? (
                                            <MuiMaterials.Tooltip title={f.tooltip} arrow placement="top">
                                                {field}
                                            </MuiMaterials.Tooltip>
                                        ) : (
                                            field
                                        )}
                                    </MuiMaterials.Box>
                                );
                            })}

                            <MuiMaterials.TextField
                                label="Raw title (optional)"
                                size="small"
                                value={rawTitle}
                                onChange={(e) => setRawTitle(e.target.value)}
                                sx={{ ...fieldSx, gridColumn: { xs: '1 / -1', md: 'span 4' }, minWidth: 80, width: '100%' }}
                            />
                            <MuiMaterials.TextField
                                label="New title (optional)"
                                size="small"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                sx={{ ...fieldSx, gridColumn: { xs: '1 / -1', md: 'span 4' }, minWidth: 80, width: '100%' }}
                            />
                            <MuiMaterials.FormControl
                                size="small"
                                sx={{
                                    ...fieldSx,
                                    marginBottom: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                    maxWidth: Math.max(
                                        80,
                                        ...['All bins', ...binNameOptions].map((n) => String(n).length * 8 + 48),
                                    ),
                                }}
                            >
                                <MuiMaterials.InputLabel id="tc-pdf-bin-filter-label">Bin</MuiMaterials.InputLabel>
                                <MuiMaterials.Select
                                    labelId="tc-pdf-bin-filter-label"
                                    label="Bin"
                                    value={filterBinName}
                                    onChange={(e) => setFilterBinName(e.target.value)}
                                    {...selectMenuProps}
                                >
                                    <MuiMaterials.MenuItem value={FILTER_ALL}>All bins</MuiMaterials.MenuItem>
                                    {binNameOptions.map((name) => (
                                        <MuiMaterials.MenuItem key={name} value={name}>
                                            {name}
                                        </MuiMaterials.MenuItem>
                                    ))}
                                </MuiMaterials.Select>
                            </MuiMaterials.FormControl>
                            <MuiMaterials.FormControl
                                size="small"
                                sx={{
                                    ...fieldSx,
                                    marginBottom: 0,
                                    gridColumn: 'span 2',
                                    minWidth: 80,
                                    width: '100%',
                                    maxWidth: Math.max(
                                        80,
                                        ...(useSiteInfosCablesUi
                                            ? ['All cables', ...siteInfosCableRows.map((r) => r.cableName)]
                                            : ['All cables', ...legacyCableNameOptions]
                                        ).map((n) => String(n).length * 8 + 48),
                                    ),
                                }}
                            >
                                <MuiMaterials.InputLabel id="tc-pdf-cable-filter-label">Cable</MuiMaterials.InputLabel>
                                <MuiMaterials.Select
                                    labelId="tc-pdf-cable-filter-label"
                                    label="Cable"
                                    value={filterCableName}
                                    onChange={(e) => handleCableFilterChange(e.target.value)}
                                    {...selectMenuProps}
                                >
                                    <MuiMaterials.MenuItem value={FILTER_ALL}>All cables</MuiMaterials.MenuItem>
                                    {useSiteInfosCablesUi
                                        ? siteInfosCableRows.map((row) => (
                                            <MuiMaterials.MenuItem key={`si:${row.id}`} value={`si:${row.id}`}>
                                                {row.cableName}
                                            </MuiMaterials.MenuItem>
                                        ))
                                        : legacyCableNameOptions.map((name) => (
                                            <MuiMaterials.MenuItem key={name} value={name}>
                                                {name}
                                            </MuiMaterials.MenuItem>
                                        ))}
                                </MuiMaterials.Select>
                            </MuiMaterials.FormControl>

                        {reportError ? (
                            <MuiMaterials.Typography
                                sx={{
                                    gridColumn: '1 / -1',
                                    color: getThemeColor('#b00020', '#ff8a80'),
                                    fontSize: '15px',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {reportError}
                            </MuiMaterials.Typography>
                        ) : null}

                        {emailFeedback ? (
                            <MuiMaterials.Typography
                                sx={{
                                    gridColumn: '1 / -1',
                                    color: emailFeedback.startsWith('Report emailed')
                                        ? getThemeColor('#2e7d32', '#a5d6a7')
                                        : getThemeColor('#b00020', '#ff8a80'),
                                    fontSize: '15px',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {emailFeedback}
                            </MuiMaterials.Typography>
                        ) : null}

                        <MuiMaterials.TextField
                            label="Email report to"
                            value={reportEmailTo}
                            onChange={(e) => setReportEmailTo(e.target.value)}
                            multiline
                            minRows={3}
                            maxRows={12}
                            size="small"
                            sx={{ ...fieldSx, gridColumn: '1 / -1', minWidth: 80, width: '100%' }}
                        />

                        {!hideFooterActions ? (
                        <MuiMaterials.Box sx={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {variant === 'modal' && typeof onModalCancel === 'function' ? (
                                <MuiMaterials.Button
                                    type="button"
                                    variant="outlined"
                                    onClick={onModalCancel}
                                    sx={{
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        borderColor: getThemeColor('#999999', '#f4cc8e'),
                                        textTransform: 'capitalize',
                                        '&:hover': { borderColor: getThemeColor('#666666', '#f4cc8e') },
                                    }}
                                >
                                    Cancel
                                </MuiMaterials.Button>
                            ) : null}
                            <MuiMaterials.LoadingButton
                                type="button"
                                loading={loadingReport}
                                variant="outlined"
                                onClick={handleViewPdf}
                                sx={outlinedActionButtonSx}
                            >
                                View
                            </MuiMaterials.LoadingButton>
                            <MuiMaterials.LoadingButton
                                type="button"
                                loading={loadingEmail}
                                variant="outlined"
                                onClick={requestEmailReport}
                                sx={outlinedActionButtonSx}
                            >
                                Email
                            </MuiMaterials.LoadingButton>
                        </MuiMaterials.Box>
                        ) : null}
                        </MuiMaterials.Box>

                        <MuiMaterials.Modal
                            open={emailConfirmOpen}
                            onClose={() => {
                                if (!loadingEmail) setEmailConfirmOpen(false);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <MuiMaterials.Box
                                sx={{
                                    background: getThemeColor('#ffffff', '#2d2d2d'),
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    borderRadius: 1,
                                    p: 2,
                                    maxWidth: 440,
                                    mx: 2,
                                    outline: 'none',
                                    border: `1px solid ${getThemeColor('#cccccc', '#f4cc8e')}`,
                                }}
                            >
                                <MuiMaterials.Typography sx={{ fontWeight: 700, marginBottom: 1 }}>
                                    Send site comparison PDF by email?
                                </MuiMaterials.Typography>
                                <MuiMaterials.Typography sx={{ marginBottom: 2, fontSize: '14px', wordBreak: 'break-word' }}>
                                    Recipients: {reportEmailTo.trim() || '(none)'}
                                </MuiMaterials.Typography>
                                <MuiMaterials.Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <MuiMaterials.Button
                                        size="small"
                                        variant="outlined"
                                        disabled={loadingEmail}
                                        onClick={() => setEmailConfirmOpen(false)}
                                        sx={outlinedActionButtonSx}
                                    >
                                        Cancel
                                    </MuiMaterials.Button>
                                    <MuiMaterials.LoadingButton
                                        loading={loadingEmail}
                                        variant="contained"
                                        onClick={confirmSendEmailReport}
                                        sx={containedActionButtonSx}
                                    >
                                        Send
                                    </MuiMaterials.LoadingButton>
                                </MuiMaterials.Box>
                            </MuiMaterials.Box>
                        </MuiMaterials.Modal>
                    </MuiMaterials.ThemeProvider>
        </>
    );

    if (variant === 'modal') {
        return (
            <MuiMaterials.Box sx={{ width: '100%' }} key={`report-tc-modal-${currentTheme}-${forceUpdate}`}>
                {formBody}
            </MuiMaterials.Box>
        );
    }

    return (
        <div>
            <MuiMaterials.Box
                key={`report-tc-pdf-${currentTheme}-${forceUpdate}`}
                className="reportTemperatureComparisonWindow hide-scrollbar"
                sx={{
                    position: 'fixed',
                    top: 0,
                    zIndex: 1000,
                    width: '100%',
                    display: variant === 'page' ? 'flex' : 'none',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    overflowX: 'hidden',
                    paddingBottom: '4px',
                    borderStyle: 'solid',
                    borderColor: getThemeColor('#cccccc', '#121212'),
                    borderWidth: '0.5px',
                    borderRadius: '4px',
                    cursor: 'default',
                    background: getThemeColor('#ffffff', '#2d2d2d'),
                    overflowY: 'auto',
                    height: variant === 'page' ? '-webkit-fill-available' : '100%',
                }}
            >
                <MuiMaterials.Box sx={{ background: getThemeColor('#e0e0e0', '#121212'), height: '44px', width: '100%' }}>
                    <MuiMaterials.Box
                        sx={{
                            background: getThemeColor('#e0e0e0', '#121212'),
                            color: getThemeColor('#333333', '#f4cc8e'),
                            display: 'flex',
                            justifyContent: 'flex-end',
                            padding: '2px 5px 1px 0px',
                            alignItems: 'center',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                left: '5px',
                                top: '3px',
                                padding: '10px 2px',
                                alignItems: 'center',
                                fontWeight: '700',
                                color: getThemeColor('#333333', '#f4cc8e'),
                            }}
                        >
                            {selectedSiteName || 'Site comparison'}
                        </div>
                        <MuiMaterials.Button
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                maxWidth: '40px',
                                maxHeight: '40px',
                                minWidth: '40px',
                                minHeight: '40px',
                                marginRight: '-6px',
                                '&:hover': { backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d') },
                            }}
                            onClick={() => {
                                const el = window.document.getElementsByClassName('reportTemperatureComparisonWindow')[0];
                                if (el) el.style.display = 'none';
                                navigate('/sites');
                            }}
                        >
                            <CloseIcon className="closeCarbonDioxide" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>

                <MuiMaterials.Box sx={{ margin: '14px 12px', maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}>
                    {formBody}
                </MuiMaterials.Box>
            </MuiMaterials.Box>
        </div>
    );
};

export default ReportTemperatureComparisonWindow;
